import Swal from "sweetalert2";

import { uploadToBlob } from "../../../lib/blobUpload";
import { ALLOWED_DOCUMENT_EXTENSIONS, validateDocument } from "../../../lib/validation";
import type { DocumentRow } from "../../../types";

import { escapeHtml } from "./escape";

export interface DocumentModalResult {
  new_name: string;
  new_description: string;
  new_expiration_date: string | null;
  /** Only set when a new file was uploaded. In edit mode without replacement,
   * the caller keeps the original URL. */
  new_document_url: string | null;
}

interface OpenArgs {
  mode: "create" | "edit";
  initial?: Pick<DocumentRow, "new_name" | "new_description" | "new_expiration_date">;
}

const accept = ALLOWED_DOCUMENT_EXTENSIONS.map((e) => `.${e}`).join(",");

export async function openDocumentModal({
  mode,
  initial,
}: OpenArgs): Promise<DocumentModalResult | null> {
  const isCreate = mode === "create";
  const result = await Swal.fire<DocumentModalResult>({
    title: isCreate ? "Add Document" : "Edit Document",
    width: 560,
    showCancelButton: true,
    confirmButtonText: isCreate ? "Add" : "Save",
    showLoaderOnConfirm: true,
    allowOutsideClick: () => !Swal.isLoading(),
    focusConfirm: false,
    html: `
      <div class="text-start">
        <div class="mb-3">
          <label class="form-label" for="modal-doc-name">
            File Name <span class="text-danger" aria-hidden="true">*</span>
          </label>
          <input id="modal-doc-name" type="text" class="form-control"
            value="${escapeHtml(initial?.new_name)}" />
        </div>
        <div class="mb-3">
          <label class="form-label" for="modal-doc-desc">
            Description <span class="text-danger" aria-hidden="true">*</span>
          </label>
          <input id="modal-doc-desc" type="text" class="form-control"
            value="${escapeHtml(initial?.new_description)}" />
        </div>
        <div class="mb-3">
          <label class="form-label" for="modal-doc-exp">Expiration Date</label>
          <input id="modal-doc-exp" type="date" class="form-control"
            value="${escapeHtml(initial?.new_expiration_date)}" />
        </div>
        <div class="mb-2">
          <label class="form-label" for="modal-doc-file">
            File ${isCreate ? '<span class="text-danger" aria-hidden="true">*</span>' : ""}
          </label>
          <input id="modal-doc-file" type="file" class="form-control"
            accept="${accept}" />
          ${
            isCreate
              ? '<small class="form-text text-muted">PDF, Office, image, txt, csv. Max 50 MB.</small>'
              : '<small class="form-text text-muted">Leave blank to keep the existing file.</small>'
          }
        </div>
      </div>
    `,
    preConfirm: async (): Promise<DocumentModalResult | false> => {
      const name = (document.getElementById("modal-doc-name") as HTMLInputElement).value.trim();
      const desc = (document.getElementById("modal-doc-desc") as HTMLInputElement).value.trim();
      const exp = (document.getElementById("modal-doc-exp") as HTMLInputElement).value || null;
      const fileInput = document.getElementById("modal-doc-file") as HTMLInputElement;
      const file = fileInput.files?.[0] ?? null;

      if (!name) {
        Swal.showValidationMessage("File Name is required");
        return false;
      }
      if (!desc) {
        Swal.showValidationMessage("Description is required");
        return false;
      }
      if (isCreate && !file) {
        Swal.showValidationMessage("Please choose a file to upload");
        return false;
      }
      if (file) {
        const err = validateDocument(file);
        if (err) {
          Swal.showValidationMessage(err);
          return false;
        }
        try {
          const { url } = await uploadToBlob(file, "document");
          return {
            new_name: name,
            new_description: desc,
            new_expiration_date: exp,
            new_document_url: url,
          };
        } catch (uploadErr) {
          const msg =
            uploadErr instanceof Error ? uploadErr.message : "Upload failed";
          Swal.showValidationMessage(`Upload failed: ${msg}`);
          return false;
        }
      }
      // Edit mode, no new file selected — caller keeps the existing URL.
      return {
        new_name: name,
        new_description: desc,
        new_expiration_date: exp,
        new_document_url: null,
      };
    },
  });
  return result.isConfirmed && result.value ? result.value : null;
}
